<template>
  <v-dialog v-model="show" max-width="800px" scrollable>
    <v-card>
      <v-card-title class="text-h5">
        <v-icon left color="primary">mdi-credit-card-outline</v-icon>
        Subscription Information
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div v-if="loading" class="text-center py-5">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
          <p class="mt-3">Loading subscription information...</p>
        </div>

        <v-alert v-if="error" type="error" class="mb-4">
          {{ error }}
        </v-alert>

        <div v-if="data && !loading">
          <!-- Organization Info -->
          <v-card outlined class="mb-4">
            <v-card-title class="subtitle-1 primary--text">
              <v-icon left small>mdi-domain</v-icon>Organization
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="6">
                  <strong>Name:</strong>
                  {{ data.organization.name }}
                </v-col>
                <v-col cols="6">
                  <strong>Organization ID:</strong>
                  {{ data.organization.orgId }}
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <div v-if="!data.hasSubscription">
            <v-alert type="info">
              <v-icon left>mdi-information</v-icon>
              {{ data.message }}
            </v-alert>
          </div>

          <div v-else>
            <!-- Subscription Details -->
            <v-card outlined class="mb-4">
              <v-card-title class="subtitle-1 success--text">
                <v-icon left small>mdi-credit-card-check</v-icon>Subscription
                Status
              </v-card-title>
              <v-card-text v-if="showDebug">
                <pre>{{ data }}</pre>
              </v-card-text>
              <v-card-text>
                <v-row>
                  <v-col cols="6">
                    <strong>Status:</strong>
                    <v-chip
                      :color="
                        data.stripe.subscription.status === 'active'
                          ? 'success'
                          : 'warning'
                      "
                      small
                      text-color="white"
                    >
                      {{ data.stripe.subscription.status }}
                    </v-chip>
                  </v-col>
                  <v-col cols="6">
                    <strong>ID:</strong>
                    {{ data.stripe.subscription.id }}
                  </v-col>
                  <!-- <v-col cols="6">
                    <strong>Current Period Start:</strong>
                    {{
                      formatDate(data.stripe.subscription.current_period_start)
                    }}
                  </v-col>
                  <v-col cols="6">
                    <strong>Current Period End:</strong>
                    {{
                      formatDate(data.stripe.subscription.current_period_end)
                    }}
                  </v-col> -->
                  <v-col
                    cols="6"
                    v-if="data.stripe.subscription.cancel_at_period_end"
                  >
                    <strong>Cancels at Period End:</strong>
                    <v-chip color="warning" small text-color="white"
                      >Yes</v-chip
                    >
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Pricing Info -->
            <v-card outlined class="mb-4">
              <v-card-title class="subtitle-1 info--text">
                <v-icon left small>mdi-currency-usd</v-icon>Pricing
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="6">
                    <strong>Amount:</strong> ${{
                      (data.stripe.pricing.amount / 100).toFixed(2)
                    }}
                  </v-col>
                  <v-col cols="6">
                    <strong>Billing:</strong>
                    {{ data.stripe.pricing.interval }}
                  </v-col>
                  <v-col cols="6">
                    <strong>Price ID:</strong>
                    {{ data.stripe.pricing.priceId }}
                  </v-col>
                  <v-col cols="6">
                    <strong>Currency:</strong>
                    {{ data.stripe.pricing.currency.toUpperCase() }}
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Customer Info -->
            <v-card outlined class="mb-4">
              <v-card-title class="subtitle-1 primary--text">
                <v-icon left small>mdi-account</v-icon>Customer
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="6">
                    <strong>Customer ID:</strong>
                    {{ data.stripe.customer.id }}
                  </v-col>
                  <v-col cols="6" v-if="data.stripe.customer.email">
                    <strong>Email:</strong>
                    {{ data.stripe.customer.email }}
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Payment Methods -->
            <v-card
              outlined
              class="mb-4"
              v-if="data.stripe.payment_methods.length > 0"
            >
              <v-card-title class="subtitle-1 purple--text">
                <v-icon left small>mdi-credit-card</v-icon>Payment Methods
              </v-card-title>
              <v-card-text>
                <div
                  v-for="pm in data.stripe.payment_methods"
                  :key="pm.id"
                  class="mb-2"
                >
                  <v-chip outlined>
                    <v-icon left small>mdi-credit-card</v-icon>
                    {{ pm.card.brand.toUpperCase() }} ••••
                    {{ pm.card.last4 }} ({{ pm.card.exp_month }}/{{
                      pm.card.exp_year
                    }})
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>

            <!-- Recent Invoices -->
            <v-card outlined v-if="data.stripe.recent_invoices.length > 0">
              <v-card-title class="subtitle-1 orange--text">
                <v-icon left small>mdi-receipt</v-icon>Recent Invoices
              </v-card-title>
              <v-card-text>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <!-- <th>Period</th> -->
                      <th>Status</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="invoice in data.stripe.recent_invoices"
                      :key="invoice.id"
                    >
                      <td>{{ formatDate(invoice.created) }}</td>
                      <!-- <td>
                        {{ formatDate(invoice.period_start) }} -
                        {{ formatDate(invoice.period_end) }}
                      </td> -->
                      <td>
                        <v-chip
                          :color="
                            invoice.status === 'paid' ? 'success' : 'warning'
                          "
                          x-small
                          text-color="white"
                        >
                          {{ invoice.status }}
                        </v-chip>
                      </td>
                      <td>${{ (invoice.amount_paid / 100).toFixed(2) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </div>

          <div class="text-caption text--secondary text-center mt-4">
            Retrieved: {{ formatDateTime(data.retrieved_at) }}
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="$emit('close')">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import firebase from "firebase/app";
import "firebase/functions";

export default {
  name: "EhcSubscriptionDialog",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    orgId: {
      type: String,
      required: true,
    },
    showDebug: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      data: null,
      error: null,
    };
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.loadSubscriptionInfo();
      }
    },
  },
  methods: {
    async loadSubscriptionInfo() {
      this.loading = true;
      this.data = null;
      this.error = null;

      try {
        const getSubscriptionInfo = firebase
          .functions()
          .httpsCallable("getSubscriptionInfo");

        const result = await getSubscriptionInfo({ orgId: this.orgId });
        this.data = result.data;
      } catch (error) {
        console.error("Error fetching subscription info:", error);
        this.error = error.message || "Failed to load subscription information";
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      if (typeof dateString === "number") {
        // Unix timestamp
        return new Date(dateString * 1000).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
      if (
        dateString === "Test" ||
        (typeof dateString === "string" &&
          isNaN(new Date(dateString).getTime()))
      ) {
        return `${dateString} (Test Data)`;
      }
      try {
        return new Date(dateString).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (e) {
        return `${dateString} (Invalid Date)`;
      }
    },
    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      try {
        return new Date(dateString).toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (e) {
        return dateString;
      }
    },
  },
};
</script>
